/**
 * `?server=http://192.168.1.10:8080` points a build at another game server: the headset at a
 * server on the PC, or the browser tests at a local one. The public site must never take it, or a
 * link to the genuine page naming someone else's host would send the sign-in form's email and
 * password there. So only a development build takes it, or a build served from this machine or
 * the local network (`vite preview`, the browser tests, a PC the headset reaches by address).
 * Returns the server's origin, or undefined when the page names none or may not take one.
 */
export function serverOverride(search: string, pageHost: string, dev: boolean): string | undefined {
  const value = new URLSearchParams(search).get('server');
  if (!value || !/^https?:\/\/[^\s/?#]+$/i.test(value)) return undefined;
  if (!dev && !isLocalNetworkHost(pageHost)) return undefined;
  try {
    return new URL(value).origin;
  } catch {
    return undefined;
  }
}

/**
 * Loopback or a private (RFC 1918) address, as `location.hostname` spells it. A name that merely
 * resolves to one does not count: whoever owns the name decides where it points.
 */
export function isLocalNetworkHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (host === 'localhost' || host.endsWith('.localhost') || host === '[::1]') return true;
  const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (!match) return false;
  const [a, b, c, d] = match.slice(1).map(Number) as [number, number, number, number];
  if ([a, b, c, d].some((octet) => octet > 255)) return false;
  return a === 127 || a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
}

/**
 * What the sign-in form tells a player of the server it sends their email and password to: that
 * there is none, or that the page's address named another than the game's own. The game's own
 * goes unsaid; its long Azure name told a player nothing (the owner, 2026-09-26).
 */
export function serverWarning(serverUrl: string, overridden: boolean): 'none' | 'custom' | null {
  if (!serverUrl) return 'none';
  return overridden ? 'custom' : null;
}

/** `https://host:8080` → `host:8080`, for telling the player where a form sends what they type. */
export function serverHost(serverUrl: string): string {
  try {
    return new URL(serverUrl).host || serverUrl;
  } catch {
    return serverUrl;
  }
}
